import { DateTime } from 'luxon';
import { sirenActions } from '~/utility/BinSentry-ui-utility';
import _ from 'lodash';
import { getOrdersAllocations, fetchAndParseEntity } from './orders-dialog-context-utilities';

const getAllSitesBins = async (siteEntity) => {
  const baseBinsEntity = await fetchAndParseEntity(siteEntity.getSubEntityByRel('https://api.binsentry.com/rel/bins').href);
  const bins = baseBinsEntity.getSubEntitiesByRel('https://api.binsentry.com/rel/bin');

  let nextHref = baseBinsEntity.getLinkByRel('next')?.href;
  while (nextHref) {
    const binsEntity = await fetchAndParseEntity(nextHref);
    bins.push(...binsEntity.getSubEntitiesByRel('https://api.binsentry.com/rel/bin'));
    nextHref = binsEntity?.getLinkByRel('next')?.href;
  }

  return bins;
};

export const getAllSiteBinEntities = (barns) => {
  return _(barns)
    .map(({ binEntities = [] }) => binEntities)
    .flatten()
    .filter()
    .value();
};

export const getBarnOrdersEntities = (barns) => {
  return _.map(barns, 'ordersEntity');
};

export const getBarnOrganizationBinEntities = (barns) => {
  return _.map(barns, 'organizationBinEntity');
};

const siteHasSuggestions = async (sitesBinsEntities) => {
  // Get the first bins orders/suggestion settings entity
  // If any bin has a suggestion settings entity, we know they all will & need to render the settings ui items
  const ordersHref = _.first(sitesBinsEntities)?.getSubEntityByRel('https://api.binsentry.com/rel/v2/orders')?.href;
  if (!ordersHref) {
    return false;
  }

  const ordersEntity = await fetchAndParseEntity(ordersHref);
  const suggestionSettingsHref = ordersEntity?.getLinkByRel('https://api.binsentry.com/rel/order-suggestion-settings')?.href;
  return !!suggestionSettingsHref;
};

export const getSitesBarnsAndPhysicalBins = async (siteEntity) => {
  const [sitesBinsEntities, sitesBarnsEntity] = await Promise.all([
    getAllSitesBins(siteEntity),
    fetchAndParseEntity(siteEntity.getSubEntityByRel('https://api.binsentry.com/rel/organization-children').href),
  ]);

  const barnEntities = sitesBarnsEntity.getSubEntitiesByRel('https://api.binsentry.com/rel/organization-child');
  const [physicalBinEntities, hasSuggestions, organizationBinEntities, ordersEntities] = await Promise.all([
    Promise.all(sitesBinsEntities.map(async (binEntity) => {
      const barnId = binEntity.getSubEntityByRel('https://api.binsentry.com/rel/owner').href;
      if (binEntity.properties.type !== 'aggregate') {
        return { barnId, binEntity };
      }
      // If a child bin is an aggregate, get all of its children
      const childBins = binEntity.getSubEntitiesByRel('https://api.binsentry.com/rel/bin-aggregate-child');
      const physicalBins = await Promise.all(
        childBins.map(async ({ href }) => await fetchAndParseEntity(href)),
      );
      return physicalBins.map(binEntity => ({ barnId, binEntity }));
    })),
    siteHasSuggestions(sitesBinsEntities),
    Promise.all(barnEntities.map(async barnEntity => {
      const barnId = barnEntity.getLinkByRel('self').href;
      const organizationBinEntity = await fetchAndParseEntity(barnEntity.getLinkByRel('https://api.binsentry.com/rel/organization-bin')?.href);
      return { barnId, organizationBinEntity };
    })),
    Promise.all(barnEntities.map(async barnEntity => {
      const barnId = barnEntity.getLinkByRel('self').href;
      const ordersEntity = await fetchAndParseEntity(barnEntity.getLinkByRel('https://api.binsentry.com/rel/orders').href);
      return { barnId, ordersEntity };
    })),
  ]);

  const binEntitiesByBarnId = _(physicalBinEntities)
    .groupBy(({ barnId }) => barnId)
    .mapValues(barnBinEntities => barnBinEntities.map(({ binEntity }) => binEntity))
    .value();

  const organizationBinEntitiesByBarnId = _(organizationBinEntities)
    .keyBy(({ barnId }) => barnId)
    .mapValues(({ organizationBinEntity }) => organizationBinEntity)
    .value();

  const orderEntitiesByBarnId = _(ordersEntities)
    .keyBy(({ barnId }) => barnId)
    .mapValues(({ ordersEntity }) => ordersEntity)
    .value();

  return {
    barnEntities,
    hasSuggestions,
    binEntitiesByBarnId,
    organizationBinEntitiesByBarnId,
    orderEntitiesByBarnId,
  };
};

async function getBinsLevelsForDate ({ bins, isoDate }) {
  const binLevels = await Promise.all(
    bins.map(async binEntity => {
      const binLevelsBaseEntity = await fetchAndParseEntity(binEntity.getSubEntityByRel('https://api.binsentry.com/rel/v2/bin-levels').href);
      const { entity: binLevelEntity } = await sirenActions.submitAction(
        binLevelsBaseEntity.getAction('get-latest-bin-level-before-date'), { date: isoDate },
      );

      return binLevelEntity;
    }),
  );

  return _(binLevels)
    // filter out empty entities from 204 responses
    .filter(item => !!item.properties)
    .keyBy(binLevel => binLevel.getSubEntityByRel('https://api.binsentry.com/rel/bin').href)
    .value();
}

async function getLatestBinLevelForecasts ({ binEntities, isoDateNoonUTC }) {
  const noonLuxon = DateTime.fromISO(isoDateNoonUTC);

  const forecasts = await Promise.all(
    binEntities.map(async binEntity => {
      const binId = binEntity.getLinkByRel('self').href;
      const statsEntity = await fetchAndParseEntity(binEntity.getSubEntityByRel('https://api.binsentry.com/rel/bin-stat-latest').href);

      // stats entity can end up being an empty siren object, so need to explicitly check for properties
      const forecasts = statsEntity?.properties?.volume?.forecast || [];
      const nearestForecast = _.orderBy(forecasts, (item) => DateTime.fromISO(item.publishedAt).diff(noonLuxon).as('seconds'),
        'desc',
      )[0];

      return { ...nearestForecast || {}, binId };
    }),
  );

  return _.keyBy(forecasts, ({ binId }) => binId);
}

async function getBarnOrganizationBinLevelAtDate ({ barnOrganizationBinEntities, isoDate }) {
  const rawLevels = await Promise.all(
    barnOrganizationBinEntities.map(async barnOrganizationBinEntity => {
      const binLevelsBaseEntity = await fetchAndParseEntity(barnOrganizationBinEntity.getSubEntityByRel('https://api.binsentry.com/rel/v2/bin-levels').href);
      const latestBinLevelHref = sirenActions.parseAction(
        binLevelsBaseEntity.getAction('get-latest-bin-level-before-date'),
        { beforeDate: isoDate },
      )?.url?.href;
      const entity = await fetchAndParseEntity(latestBinLevelHref);
      return entity;
    }),
  );

  // filter out empty entities from 204 responses
  return rawLevels.filter(item => !!item.properties);
}

async function fetchDataForDate({ barns, siteEntity, isoDateNoonUTC }) {
  const allSiteBinEntities = getAllSiteBinEntities(barns);
  const allBarnOrdersEntities = getBarnOrdersEntities(barns);
  const allBarnOrganizationBinEntities = getBarnOrganizationBinEntities(barns);
  const [
    forecastsByBinId,
    binLevelsByBinId,
    feedOrdersEntity,
    { daysOrders, daysAllocations },
    barnBinLevelEntities,
  ] = await Promise.all([
    getLatestBinLevelForecasts({ binEntities: allSiteBinEntities, isoDateNoonUTC }),
    getBinsLevelsForDate({ bins: allSiteBinEntities, isoDateNoonUTC }),
    getFeedOrderForDay({ siteEntity, isoDateNoonUTC }),
    getOrdersForDay({ siteEntity, ordersEntities: allBarnOrdersEntities, isoDateNoonUTC }),
    getBarnOrganizationBinLevelAtDate({ barnOrganizationBinEntities: allBarnOrganizationBinEntities, isoDate: isoDateNoonUTC }),
  ]);

  return {
    forecastsByBinId,
    binLevelsByBinId,
    feedOrdersEntity,
    daysOrders,
    daysAllocations,
    barnBinLevelEntities,
  };
}

async function getFeedOrderForDay({ siteEntity, isoDateNoonUTC }) {
  const feedOrderId = siteEntity.getLinkByRel('https://api.binsentry.com/rel/feed-orders')?.href;
  if (!feedOrderId) {
    return;
  }
  // get the feed-order for the specific day
  const feedOrdersBaseEntity = await fetchAndParseEntity(feedOrderId);
  const { entity: feedOrdersEntity } = await sirenActions.submitAction(
    feedOrdersBaseEntity.getAction('get-feed-order'), { date: isoDateNoonUTC },
  );

  return feedOrdersEntity;
}

async function getOrdersForDay({ ordersEntities, isoDateNoonUTC }) {
  const response = await Promise.all(
    ordersEntities.map(async (orderBaseEntity) => {
      const ordersSearchHref = sirenActions.parseAction(
        orderBaseEntity.getAction('get-all-by-date-range'),
        {
          rangeStartIsoDate: DateTime.fromISO(isoDateNoonUTC).setZone('utc').minus({ hours: 12 }).toISO(),
          rangeEndIsoDate: DateTime.fromISO(isoDateNoonUTC).setZone('utc').plus({ hours: 12 }).toISO(),
        },
        { appendArrays: true },
      )?.url?.href;
      const entity = await fetchAndParseEntity(ordersSearchHref, true);

      return entity.getSubEntitiesByRel('https://api.binsentry.com/rel/order');
    }),
  );

  const allOrders = _.flatten(response);
  const daysOrders = allOrders.filter(order => order.properties.state !== 'closed');
  const daysAllocations = _(daysOrders).map(orderEntity => getOrdersAllocations(orderEntity)).flatten().value();

  return {
    daysOrders,
    daysAllocations,
  };
}

export async function getSelectedDateData ({
  isoDate,
  barns,
  siteEntity,
}) {
  const initialDateNoon = DateTime.fromISO(isoDate).set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
  const isoDateNoon = initialDateNoon.toISO();
  const isoDateNoonUTC = initialDateNoon.toUTC().toISO();

  const {
    forecastsByBinId,
    binLevelsByBinId,
    feedOrdersEntity,
    daysOrders,
    daysAllocations,
    barnBinLevelEntities,
  } = await fetchDataForDate({ barns, siteEntity, isoDateNoonUTC });

  return {
    forecastsByBinId,
    binLevelsByBinId,
    feedOrdersEntity,
    daysOrders,
    daysAllocations,
    barnBinLevelEntities,
    isoDateNoon,
    isoDateNoonUTC,
  };
}
